import { memo, useCallback } from 'react';

import { Label, Separator, Text, TextField } from '@/components/core';
import { TextArea } from '@/components/core';
import { findField, FormatterValidatorHeader } from '@/components/ui';
import { useDebounceCallback, useFormatterMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';

import { FormatterTest } from './FormatterTest';

export type RegexFormatterProps = {
  fieldId: string;
  formatterId: string;
};

const IRegexFormatter = ({ fieldId, formatterId }: RegexFormatterProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const formatter = field?.formatters?.find((f) => f.id === formatterId);

  const mutate = useFormatterMutation({ fieldId, formatterId, parentId: field?.parentId });

  const onSetMatchPattern = useDebounceCallback(
    useCallback((matchPattern: string) => mutate((f) => (f.config.matchPattern = matchPattern)), [mutate]),
    500
  );

  const onSetReplacePattern = useDebounceCallback(
    useCallback((replacePattern: string) => mutate((f) => (f.config.replacePattern = replacePattern)), [mutate]),
    500
  );

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <FormatterValidatorHeader formatter={formatter} />

      <Separator orientation="horizontal" />
      <div className="flex grow flex-col gap-4">
        <TextField onChange={onSetMatchPattern} defaultValue={formatter?.config.matchPattern?.toString()}>
          <Label>Match pattern</Label>
          <TextArea placeholder="Example: ^(.*) Corp$" />
        </TextField>

        <TextField onChange={onSetReplacePattern} defaultValue={formatter?.config.replacePattern?.toString()}>
          <Label>Replace pattern</Label>
          <TextArea placeholder="Example: $1 Corporation" />
        </TextField>

        <Text slot="description">
          See{' '}
          <a
            className="underline"
            href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace"
          >
            here
          </a>{' '}
          and{' '}
          <a
            className="underline"
            href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions/Cheatsheet"
          >
            here
          </a>{' '}
          for more details and examples.
        </Text>
      </div>

      <FormatterTest fieldId={fieldId} formatterId={formatterId} />
    </div>
  );
};

export const RegexFormatter = memo(IRegexFormatter) as typeof IRegexFormatter;
